import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useSetSelectTheme } from "@/contexts/SelectThemeContext";
import { toast } from "sonner";
import { Search, Plus, AlertCircle, AlertTriangle, Check, BellRing, BellOff, ShieldCheck, Package } from "lucide-react";
import OptInNudge from "@/components/OptInNudge";
import { isProductOutOfStock as computeOutOfStock } from "@/lib/stock";
import { selectStockBadge, lowestTrackedVials, totalActualStock } from "@/lib/stockBadge";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import SupplyCatalogTiered from "@/pages/SupplyCatalogTiered";
import { PREVIEW_HOSTS } from "@/components/ProtectedRoute";
import ProductDetail from "@/pages/ProductDetail";

const TIER_LABELS = { supply: "Research Supply", select: "Research Select" };

// iter201 · Housekeeping — direct-PDF COA badge. Bypasses the intermediary
// /coa/{slug} viewer entirely: single-COA products link straight to the
// backend inline-PDF endpoint in a new tab; multi-COA products expose a
// small hover chooser that also opens the chosen PDF directly.
function CoaBadge({ product, select }) {
  const API = process.env.REACT_APP_BACKEND_URL || "";
  const coas = product.coas || [];
  const directHref = (coa) => `${API}/api/products/${product.id}/coa/${coa.id}`;
  const legacyHref = `${API}/api/products/${product.id}/coa`;
  const cls = `inline-flex items-center gap-1 text-[11px] font-mono font-medium uppercase tracking-[0.14em] px-2.5 py-1 rounded-full transition-colors ${
    select
      ? "bg-[#0F5F3B] hover:bg-[#0A4A2C] text-bone border border-[#0F5F3B]"
      : "bg-emerald-700 hover:bg-emerald-800 text-bone border border-emerald-800"
  }`;
  const stop = (e) => e.stopPropagation();
  if (coas.length <= 1) {
    // Legacy single-COA products (no `coas[]` list, only `has_coa` + storage
    // path) fall through to the /coa endpoint which returns the first COA.
    const href = coas.length === 1 ? directHref(coas[0]) : legacyHref;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stop}
        className={cls}
        data-testid={`coa-badge-${product.slug}`}
        title="View Certificate of Analysis (opens in new tab)"
      >
        <ShieldCheck size={12} strokeWidth={2.4} />
        COA
      </a>
    );
  }
  // Multi-COA — smallest possible chooser via existing HoverCard primitive.
  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={stop}
          className={cls}
          data-testid={`coa-badge-${product.slug}`}
          title="Choose a Certificate of Analysis"
        >
          <ShieldCheck size={12} strokeWidth={2.4} />
          COA
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="p-2 w-[260px] bg-white border-borderSubtle">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-charcoal/60 px-2 pt-1 pb-2">
          Choose certificate
        </div>
        <div className="flex flex-col gap-1">
          {coas.map((c) => (
            <a
              key={c.id}
              href={directHref(c)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stop}
              className="px-2 py-1.5 text-[12px] text-charcoal hover:bg-bone rounded-sm truncate"
              data-testid={`coa-choice-${product.slug}-${c.id}`}
              title={c.filename || `COA ${c.id}`}
            >
              {c.filename || `Certificate ${c.id}`}
            </a>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

// Strip trailing "NNmg" suffix from a Select-tier display name so catalog cards
// and PDPs read the same short brand name (e.g. "GHK-CU 50mg" → "GHK-CU").
const stripMgSuffix = (name) => String(name || "").replace(/\s*\d+\s*mg\s*$/i, "").trim();

/** Tiny circular US-flag disc, used inline in badges (avoids emoji fonts). */
function UsFlagBadge({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ display: "inline-block" }}>
      <defs>
        <clipPath id="usflag-badge-clip">
          <circle cx="12" cy="12" r="11" />
        </clipPath>
      </defs>
      <g clipPath="url(#usflag-badge-clip)">
        <rect x="0" y="0" width="24" height="24" fill="#F5EFE5" />
        {[1, 3, 5, 7, 9, 11].map((y) => (
          <rect key={y} x="0" y={y * 2} width="24" height="2" fill="#B22234" />
        ))}
        <rect x="0" y="0" width="11" height="12" fill="#3C3B6E" />
      </g>
    </svg>
  );
}

/**
 * iter156.9 — Neutralized. The "Research Use Only · Untested" hover warning
 * was removed at the site owner's direction (all BBK products are tested;
 * the warning was factually incorrect and eroding buyer confidence). Kept as
 * a passthrough so the two call-sites in Catalog stay unchanged and any
 * future re-introduction is a one-liner.
 */
function SupplyUntestedHover({ children }) {
  return children;
}

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("ALL");
  const [waitlistIds, setWaitlistIds] = useState(new Set()); // product ids the buyer is waitlisted for
  const [waitlistBusy, setWaitlistBusy] = useState(new Set()); // product ids with an in-flight toggle
  // "Now Live" ribbon flag for the Select-tier launch — admin-toggled in Settings.
  const [selectNowLive, setSelectNowLive] = useState(false);
  // Two-stage inventory countdown thresholds (Select tier only). Fetched
  // alongside the launch flag from /landing/settings — same admin surface.
  const [stockThresholds, setStockThresholds] = useState({
    lowStockThreshold: 20,
    limitedStockThreshold: 30,
  });
  const { add, count, MIN_KITS, kitsRemaining, meetsMinimum } = useCart();
  const { user } = useAuth();
  const isTempViewer = user?.role === "temp_viewer";
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const tier = (searchParams.get("tier") || "").toLowerCase();
  const tierLabel = TIER_LABELS[tier] || null;
  const select = tier === "select";
  // iter148p — Supply tier temporarily paused. Hard-block direct URL access so
  // bookmarks/deep-links can't bypass the Chooser banner. Toggle this flag off
  // when Supply reopens (paired with removing `comingSoon` on Chooser.jsx).
  const SUPPLY_PAUSED = !PREVIEW_HOSTS.has(window.location.hostname);
  useEffect(() => {
    if (SUPPLY_PAUSED && tier === "supply" && !isTempViewer && user?.role !== "admin") {
      nav("/chooser", { replace: true });
    }
  }, [SUPPLY_PAUSED, tier, isTempViewer, user?.role, nav]);
  // Activate the R-1617 sub-brand skin when browsing the Select tier catalog.
  useSetSelectTheme(select);

  // iter_142 — Bare `/shop` (no ?tier=) used to fetch the whole /products
  // endpoint UNFILTERED, which leaked Select products into the Supply shelf
  // and vice-versa. Any of these paths hits it: Cart "continue browsing",
  // Order confirmation, BrandMark logo click, /orders "browse the catalog",
  // BuildAKit "back". Now: resolve the intended tier (URL → localStorage →
  // default Supply) and immediately redirect the URL so all downstream
  // fetches and links carry `?tier=`.
  const TIER_MEMORY_KEY = "bbk_last_tier";
  useEffect(() => {
    if (tier === "supply" || tier === "select") {
      try { localStorage.setItem(TIER_MEMORY_KEY, tier); } catch { /* no-op */ }
      return;
    }
    // Temp-viewers are shown a curated cross-tier list — don't force a redirect.
    if (isTempViewer) return;
    let remembered = "";
    try { remembered = (localStorage.getItem(TIER_MEMORY_KEY) || "").toLowerCase(); } catch { /* no-op */ }
    const target = remembered === "select" ? "select" : "supply";
    // Preserve any other query params (search, category filters, etc.).
    const params = new URLSearchParams(searchParams);
    params.set("tier", target);
    nav(`/shop?${params.toString()}`, { replace: true });
  }, [tier, isTempViewer, nav, searchParams]);

  // Build a tier-preserving PDP href for every product card link. Missing
  // this suffix is how "ghost pages" happen (Supply card → Select PDP), since
  // the backend can't disambiguate a shared-slug row without the ?tier= hint.
  const pdpHref = (slug) => {
    if (tier === "supply" || tier === "select") return `/shop/${slug}?tier=${tier}`;
    return `/shop/${slug}`;
  };
  // Same tier suffix for the branded COA viewer — otherwise Select products
  // 404 in the viewer because their slug also exists on Supply.
  const coaHref = (slug) => {
    if (tier === "supply" || tier === "select") return `/coa/${slug}?tier=${tier}`;
    return `/coa/${slug}`;
  };

  useEffect(() => {
    // iter_142 — skip the fetch entirely when tier is empty. The redirect
    // effect above will re-fire this with a resolved tier immediately.
    // Prevents a flash of mixed-tier products before the redirect commits.
    if (!(tier === "supply" || tier === "select") && !isTempViewer) return;
    setLoading(true);
    (async () => {
      try {
        const productsUrl = tier && (tier === "supply" || tier === "select") ? `/products?tier=${tier}` : "/products";
        const [{ data: prods }, { data: wl }, settingsRes] = await Promise.all([
          api.get(productsUrl),
          api.get("/oos-waitlist/mine").catch(() => ({ data: { product_ids: [] } })),
          // Landing settings carries the `select_now_live_enabled` launch flag.
          // Failure is non-fatal — the ribbon just doesn't show.
          api.get("/landing/settings").catch(() => ({ data: {} })),
        ]);
        const sorted = [...prods].sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true }));
        setProducts(sorted);
        setWaitlistIds(new Set(wl?.product_ids || []));
        setSelectNowLive(!!settingsRes?.data?.select_now_live_enabled);
        setStockThresholds({
          lowStockThreshold: Number(settingsRes?.data?.low_stock_threshold ?? 20),
          limitedStockThreshold: Number(settingsRes?.data?.limited_stock_threshold ?? 30),
        });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier, isTempViewer]);

  const toggleNotify = async (p) => {
    if (waitlistBusy.has(p.id)) return;
    setWaitlistBusy((prev) => new Set(prev).add(p.id));
    const wasOn = waitlistIds.has(p.id);
    try {
      if (wasOn) {
        await api.delete(`/products/${p.id}/notify-me`);
        setWaitlistIds((prev) => { const n = new Set(prev); n.delete(p.id); return n; });
        toast.success("You won't be notified when this comes back.");
      } else {
        await api.post(`/products/${p.id}/notify-me`);
        setWaitlistIds((prev) => new Set(prev).add(p.id));
        toast.success(`We'll email you when ${p.name} is back in stock.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    } finally {
      setWaitlistBusy((prev) => { const n = new Set(prev); n.delete(p.id); return n; });
    }
  };

  const categories = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean));
    return ["ALL", ...Array.from(set)];
  }, [products]);

  const featured = useMemo(() => {
    // Only surface featured hero when catalog isn't filtered/searched — otherwise it feels out of place
    if (q.trim() || cat !== "ALL") return null;
    return products.find((p) => p.featured) || null;
  }, [products, q, cat]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter((p) => {
      if (featured && p.id === featured.id) return false; // hoist the featured to hero
      if (cat !== "ALL" && p.category !== cat) return false;
      if (!needle) return true;
      // Name-only search on the public catalog. SKU is admin-internal and
      // varies per mg-variant, so surfacing it here just adds noise for buyers.
      return p.name.toLowerCase().includes(needle);
    });
  }, [products, q, cat, featured]);

  const addToCart = (e, p) => {
    e.stopPropagation();
    // Route through the shared helper so multi-variant products with at least
    // one in-stock variant still let the buyer add. Product-level in_stock is
    // ignored when variants exist — matches the button/badge logic above.
    if (computeOutOfStock(p)) {
      toast.error("Out of stock");
      return;
    }
    if (add(p, 1) === false) return;
    toast.success(`${p.name} added`);
  };

  // iter184 Task 4 — Supply tier catalog now delegates to the tier-grouped
  // SupplyCatalogTiered component. Placed AFTER all hooks so React
  // hooks-order rules stay satisfied. Bypasses SUPPLY_PAUSED for admins/
  // temp-viewers so they can iterate on the design pre-relaunch.
  if (tier === "supply" && (!SUPPLY_PAUSED || user?.role === "admin" || isTempViewer)) {
    return <SupplyCatalogTiered />;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 lg:py-14" data-testid="catalog-page">
      {isTempViewer && (
        <div
          className="mb-6 border border-oxblood/40 bg-oxblood/5 rounded-[3px] px-5 py-4"
          data-testid="temp-preview-banner"
        >
          <p className="text-[13px] uppercase tracking-[0.28em] font-mono text-oxblood mb-1">Preview access</p>
          <p className="text-sm text-charcoal/85 leading-relaxed">
            You're viewing a curated preview. Cart and checkout are disabled on this account — reach out to your contact at Bulk Buy Kits to place an order.
          </p>
        </div>
      )}
      {/* Launch ribbon — Select tier only, admin-toggled. Announces the
          Research 1617 line is open for returning buyers. */}
      {select && selectNowLive && (
        <div
          className="mb-6 flex items-center justify-center gap-3 px-6 py-3 rounded-[3px] border"
          style={{
            background: "linear-gradient(90deg, rgba(221,169,78,0.10), rgba(221,169,78,0.22), rgba(221,169,78,0.10))",
            borderColor: "rgba(221,169,78,0.55)",
          }}
          data-testid="select-launch-ribbon"
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: "#DDA94E", boxShadow: "0 0 10px #DDA94E" }}
            aria-hidden
          />
          <span className="text-[13px] md:text-[12px] uppercase tracking-[0.34em] font-mono text-[#6E521A]">
            Now Live
          </span>
          <span className="hidden sm:inline text-[13px] tracking-[0.14em] text-[#1B1614]/80">
            · Research 1617 is now open — a smaller-scale line for focused research programs.
          </span>
        </div>
      )}
      {/* Header */}
      {tierLabel && (
        <div className="mb-10 flex items-center gap-3" data-testid="catalog-tier-badge">
          <span
            className={`inline-flex items-center rounded-full font-mono ${
              select
                ? "bg-[#DDA94E] text-black px-5 py-2 text-[15px] uppercase tracking-[0.32em] font-semibold"
                : "bg-charcoal text-bone px-3 py-1 text-[12px] uppercase tracking-[0.22em]"
            }`}
          >
            {tierLabel}
          </span>
          <Link
            to="/chooser"
            className={`hidden text-[12px] uppercase tracking-[0.22em] transition-colors ${
              select ? "text-[#6E521A] hover:text-[#6E521A]" : "text-stone hover:text-oxblood"
            }`}
            data-testid="catalog-switch-tier"
          >
            Switch tier →
          </Link>
        </div>
      )}
      {/* Hero block — Supply tier only. Removed on Select 2026-02 (user
          request): the H1 + description + shipping hint + price disclaimer +
          hairline all created visual noise above the product grid. Select tier
          jumps straight into product cards. */}
      {!select && (
        <>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-3">
            Bulk Buy
          </h1>
          <p className="text-base md:text-lg max-w-2xl mb-2 text-stone">
            Pre-packaged research kits available in bulk quantities. Pricing reflects a full 10-vial kit.
          </p>
          <p
            className="text-[13px] uppercase tracking-[0.22em] mb-6 flex items-center gap-2 text-stone"
            data-testid="catalog-price-disclaimer"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-stone/60"></span>
            Prices subject to change without notice
          </p>
          <div className="hairline mb-8" />
        </>
      )}

      {/* BYOK ribbon — iter142 Phase 1B — solid gold bar with dark ink so it
          POPS against the cream ground instead of blending in. It's a feature,
          not a footnote. Hidden for temp-viewers. */}
      {select && !isTempViewer && (
        <Link
          to="/shop/build"
          className="flex items-center justify-between gap-4 mb-6 rounded-full px-5 py-3 bg-[#DDA94E] border border-[#B58524] hover:bg-[#E7B85B] hover:shadow-[0_4px_20px_-4px_rgba(221,169,78,0.55)] transition-all group"
          data-testid="byok-banner"
        >
          <div className="flex items-baseline gap-3 min-w-0 flex-wrap">
            <span className="font-serif text-[20px] md:text-[22px] text-[#1B1614] font-semibold truncate">Build Your Own Research Kit</span>
            <span className="text-[14px] md:text-[15px] text-[#1B1614]/85 hidden md:inline">· 3 or 5 vials, mix &amp; match</span>
          </div>
          <span
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase px-4 py-1.5 rounded-full bg-[#1B1614] text-[#F1E5C7] font-semibold group-hover:bg-[#F1E5C7] group-hover:text-[#1B1614] transition-colors shrink-0"
            data-testid="byok-banner-cta"
          >
            Start Building →
          </span>
        </Link>
      )}

      {/* iter185.4 — Minimum-kit banner removed (single kit checkout is now
          allowed on both tiers). Only the tier-mismatch guard is retained. */}
      {!isTempViewer && (() => {
        const catalogTier = select ? "select" : "supply";
        // Read the tier of items currently in cart (if any)
        const cartLineTier = (typeof window !== "undefined") ? (JSON.parse(localStorage.getItem("peptidelab_cart_v1") || "[]")[0]?.tier || null) : null;
        const mismatch = cartLineTier && cartLineTier !== catalogTier && count > 0;
        if (!mismatch) return null;
        const otherName = cartLineTier === "select" ? "Research 1617" : cartLineTier === "10vk" ? "10-Vial Kits" : "Research Supply";
        const thisName = catalogTier === "select" ? "Research 1617" : "Research Supply";
        return (
          <div
            className={`rounded-xl px-5 py-4 mb-8 flex flex-wrap items-center justify-between gap-4 ${
              select ? "bg-[#FDECEA] border border-oxblood/50" : "bg-[#FDECEA] border border-oxblood/40"
            }`}
            data-testid="catalog-tier-mismatch-banner"
          >
            <div className="flex items-center gap-3">
              <AlertCircle size={18} strokeWidth={2} className="text-oxblood shrink-0"/>
              <div className="text-sm">
                <p className={`font-semibold ${select ? "text-[#1B1614]" : "text-oxblood"}`}>Your cart has {otherName} items.</p>
                <p className={select ? "text-[#6E521A]" : "text-stone"}>
                  Please complete or clear that cart before shopping {thisName}.
                </p>
              </div>
            </div>
            <Link
              to="/cart"
              className="text-[13px] uppercase tracking-[0.22em] bg-oxblood text-bone px-5 py-2.5 rounded-full hover:bg-charcoal transition-colors"
              data-testid="catalog-tier-mismatch-view-cart"
            >
              View cart →
            </Link>
          </div>
        );
      })()}

      {/* Search */}
      <div className="mb-6 relative">
        <Search size={18} strokeWidth={1.5} className={`absolute left-5 top-1/2 -translate-y-1/2 ${select ? "text-[#6E521A]" : "text-stone"}`} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search products by name…"
          className={`w-full h-14 pl-12 pr-5 rounded-full text-base focus:outline-none transition-colors ${
            select
              ? "bg-white/70 border border-[#D6C8A3] text-[#1B1614] placeholder:text-[#6E521A] focus:border-[#DDA94E]"
              : "bg-white border border-borderSubtle placeholder:text-stone focus:border-charcoal"
          }`}
          data-testid="catalog-search"
        />
      </div>

      {/* Categories chips + disclaimer removed 2026-02 for compliance (client request). */}
      <div className="mb-6" />


      {/* Featured hero tile — reuses the standalone PDP layout via embedded
          mode so the Featured product has the SAME image proportions, badge
          row, variant selector, 1/3/5 vial controls, SAVE badges, qty +/-,
          and ADD-to-cart behavior. Featured-specific additions (★ Featured
          image pill, HOT SELLER → LYO USA → COA badge order) are handled
          inside ProductDetail when props embedded + featured are passed. */}
      {featured && (
        <div className="mb-10 animate-fade-up" data-testid="featured-hero">
          <ProductDetail slug={featured.slug} tier={featured.tier} embedded featured />
        </div>
      )}

      {loading ? (
        <p className={`overline ${select ? "text-[#6E521A]" : ""}`} data-testid="catalog-loading">Loading catalog…</p>
      ) : filtered.length === 0 ? (
        <p className={`overline ${select ? "text-[#6E521A]" : ""}`} data-testid="catalog-empty">No kits match your filter.</p>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          data-testid="catalog-grid"
        >
          {filtered.map((p, idx) => {
            const outOfStock = computeOutOfStock(p);
            return (
            <div
              key={p.id}
              onClick={() => nav(pdpHref(p.slug))}
              className={`group relative rounded-2xl cursor-pointer transition-all animate-fade-up ${
                select
                  ? "bg-[#E8DDBB] border border-[#DDA94E]/60 hover:border-[#DDA94E] hover:bg-[#EDDFB5] hover:shadow-[0_4px_28px_-6px_rgba(221,169,78,0.35)] hover:![transform:translateY(-3px)] duration-200 p-3.5 pt-2.5"
                  : "bg-[#EFEAE0] hover:shadow-[0_2px_20px_-4px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 p-4 pt-3"
              } ${outOfStock ? "opacity-70" : ""}`}
              style={{ animationDelay: `${idx * 40}ms` }}
              data-testid={`product-card-${p.slug}`}
            >
              {/* iter206 — badge row: OOS/Hot Seller/Single Buy on the left,
                  COA + LYO USA pushed to the right side via ml-auto. */}
              <div className="flex items-center flex-wrap gap-2">
              {/* SKU pill — admin-internal only. Hidden on the public
                  catalog because SKUs vary per mg-variant and add clutter.
                  If you need it back, remove the `hidden` class. */}
              <span
                className={`hidden text-[12px] font-mono uppercase tracking-widest px-3 py-1 rounded-full ${
                  select ? "bg-[#EBE0BE] border border-[#DDA94E]/40 text-[#6E521A]" : "bg-charcoal text-bone"
                }`}
                data-testid={`sku-${p.slug}`}
              >
                {p.sku}
              </span>
              {outOfStock && (
                <span
                  className="inline-block bg-oxblood text-bone text-[12px] font-mono uppercase tracking-widest px-3 py-1 rounded-full"
                  data-testid={`oos-badge-${p.slug}`}
                >
                  Out of stock
                </span>
              )}
              {/* iter204.4 — top-of-card inventory pill removed in iter204.5.
                  Inventory now lives in the bottom info row next to the
                  product-type label (see below). */}
              {/* Iter118 → iter155 — Inventory countdown badge on BOTH tiers.
                  Removed from catalog cards per user request (iter148); stock shown on PDP only. */}
              {false && !outOfStock && (() => {
                const tracked = lowestTrackedVials(p);
                if (!tracked) return null;
                const perLow = p.low_stock_threshold;
                const thresh = perLow != null
                  ? { lowStockThreshold: Number(perLow), limitedStockThreshold: Math.max(Number(perLow) + 10, stockThresholds.limitedStockThreshold) }
                  : stockThresholds;
                const badge = selectStockBadge(tracked.count, {
                  ...thresh,
                  mgLabel: tracked.mgLabel,
                });
                if (!badge) return null;
                const isLow = badge.level === "low";
                const cls = select
                  ? (isLow
                    ? "bg-[#DDA94E] text-black border-[#DDA94E]"
                    : "bg-[#EBE0BE] text-[#6E521A] border-[#DDA94E]/60")
                  : (isLow
                    ? "bg-oxblood text-bone border-oxblood"
                    : "bg-bone text-oxblood border-oxblood/50");
                return (
                  <span
                    className={`inline-block text-[12px] font-mono uppercase tracking-widest px-3 py-1 rounded-full border ${cls}`}
                    data-testid={`stock-badge-${p.slug}`}
                    data-level={badge.level}
                  >
                    {badge.label}
                  </span>
                );
              })()}
              {p.is_hot_seller && (
                <span
                  className={`inline-flex items-center gap-1 text-[12px] font-mono uppercase tracking-widest px-3 py-1 rounded-full ${
                    select ? "bg-[#DDA94E] text-black" : "bg-oxblood text-bone"
                  }`}
                  data-testid={`hot-seller-badge-${p.slug}`}
                  title="Hot seller"
                >
                  🔥 Hot Seller!
                </span>
              )}
              {p.single_buy && (
                <span
                  className={`inline-flex items-center gap-1 text-[12px] font-mono uppercase tracking-widest px-3 py-1 rounded-full ${
                    select
                      ? "bg-[#EBE0BE] border border-[#DDA94E]/50 text-[#6E521A]"
                      : "bg-white border border-charcoal text-charcoal"
                  }`}
                  data-testid={`single-buy-badge-${p.slug}`}
                  title="No minimum on this product — order a single unit."
                >
                  Single Buy OK
                </span>
              )}
              <div className="ml-auto flex items-center gap-2">
                {p.has_coa && (
                  <CoaBadge product={p} select={select} />
                )}
                {select && p.is_lyo_usa && (
                  <span
                    className={`inline-flex items-center gap-1 text-[12px] font-mono uppercase tracking-widest px-3 py-1 rounded-full ${
                      select
                        ? "bg-[#EBE0BE] border border-[#DDA94E]/50 text-[#1B1614]"
                        : "bg-white border border-borderSubtle text-charcoal"
                    }`}
                    data-testid={`lyo-usa-badge-${p.slug}`}
                    title="US Lyophilization"
                    aria-label="Lyophilized in the USA"
                  >
                    <UsFlagBadge />
                    Lyo USA
                  </span>
                )}
              </div>
              </div>

              {/* Name */}
              <h3 className={`font-serif font-semibold tracking-tight ${select ? "text-[17px] leading-[18px] mt-2 text-[#1B1614]" : "text-2xl leading-[1.15] mt-3"}`}>{select ? stripMgSuffix(p.name) : p.name}</h3>

              {/* Unit label — hidden on Select tier per user direction */}
              {!select && <p className={`text-sm mt-1 text-stone`}>{p.unit_label}</p>}

              {/* Price row */}
              <div className={`flex items-center justify-between ${select ? "mt-2.5" : "mt-4"}`}>
                {(() => {
                  // mg-variants take priority (both tiers). Card shows
                  // "Starting at $X · N options available" + "View Options".
                  const variantsList = Array.isArray(p.variants) ? p.variants : [];
                  const availableMg = variantsList.filter((v) => v && v.price != null);
                  if (availableMg.length > 0) {
                    // Per-vial starting price = min variant.price. Select-tier
                    // buyer sees the ladder-discounted tile prices on the PDP.
                    const startingPrice = Math.min(...availableMg.map((v) => Number(v.price)));
                    const optionCount = availableMg.length;
                    return (
                      <>
                        <div>
                          <p className={`font-mono ${select ? "text-[11px] tracking-[0.2em] mb-0 text-[#6E521A]" : "text-[12px] tracking-[0.22em] mb-0.5 text-stone"} uppercase`} data-testid={`mg-options-count-${p.slug}`}>
                            {optionCount} option{optionCount !== 1 ? "s" : ""} available
                          </p>
                          <p className={`font-mono uppercase ${select ? "text-[11px] tracking-[0.2em] text-[#6E521A]" : "text-[12px] tracking-[0.22em] text-stone"}`}>Starting at</p>
                          <p className={`font-semibold font-mono ${select ? "text-[1.375rem] leading-none mt-0.5 text-[#6E521A]" : "text-2xl"}`} data-testid={`mg-starting-price-${p.slug}`}>${startingPrice.toFixed(2)}</p>
                        </div>
                        <Link
                          to={pdpHref(p.slug)}
                          onClick={(e) => e.stopPropagation()}
                          className={`inline-flex items-center gap-1.5 rounded-full uppercase tracking-[0.18em] transition-colors ${
                            select ? "px-3.5 py-1.5 text-[12px]" : "px-4 py-2 text-[13px]"
                          } ${
                            outOfStock
                              ? "bg-stone/40 text-bone cursor-not-allowed pointer-events-none"
                              : select
                                ? "bg-[#DDA94E] hover:bg-[#E8B857] text-black"
                                : "bg-charcoal hover:bg-oxblood text-bone"
                          }`}
                          data-testid={`view-options-btn-${p.slug}`}
                        >
                          {outOfStock ? "Unavailable" : "View Options"}
                        </Link>
                      </>
                    );
                  }
                  // Select-tier card without mg variants: single-option
                  // treatment — show a straightforward price only (no
                  // "1 option available", no "Starting at") and a
                  // "View Product" CTA per user site-optimization spec.
                  if (select) {
                    const startingPrice = Number(p.price || 0);
                    return (
                      <>
                        <div>
                          <p className="text-[1.375rem] font-semibold font-mono leading-none text-[#6E521A]" data-testid={`select-starting-price-${p.slug}`}>${startingPrice.toFixed(2)}</p>
                        </div>
                        <Link
                          to={pdpHref(p.slug)}
                          onClick={(e) => e.stopPropagation()}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] uppercase tracking-[0.18em] transition-colors ${
                            outOfStock
                              ? "bg-stone/40 text-bone cursor-not-allowed pointer-events-none"
                              : "bg-[#DDA94E] hover:bg-[#E8B857] text-black"
                          }`}
                          data-testid={`select-choose-btn-${p.slug}`}
                        >
                          {outOfStock ? "Unavailable" : "View Product"}
                        </Link>
                      </>
                    );
                  }
                  // Supply tier (or legacy null tier) — original single-price Add button
                  return (
                    <>
                      <p className="text-2xl font-semibold font-mono">${p.price.toFixed(2)}</p>
                      {isTempViewer ? (
                        <span
                          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] uppercase tracking-[0.18em] bg-stone/40 text-bone cursor-not-allowed"
                          data-testid={`add-btn-preview-${p.slug}`}
                        >
                          Preview only
                        </span>
                      ) : (
                        <SupplyUntestedHover active={!select && !outOfStock}>
                          <button
                            onClick={(e) => addToCart(e, p)}
                            disabled={outOfStock}
                            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] uppercase tracking-[0.18em] transition-colors ${
                              outOfStock
                                ? "bg-stone/40 text-bone cursor-not-allowed"
                                : "bg-charcoal hover:bg-oxblood text-bone"
                            }`}
                            data-testid={`add-btn-${p.slug}`}
                          >
                            <Plus size={12} strokeWidth={2.5} />
                            {outOfStock ? "Unavailable" : "Add"}
                          </button>
                        </SupplyUntestedHover>
                      )}
                    </>
                  );
                })()}
              </div>

              {outOfStock && (
                (() => {
                  const notified = waitlistIds.has(p.id);
                  const busy = waitlistBusy.has(p.id);
                  return (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleNotify(p); }}
                      disabled={busy}
                      className={`mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-[13px] uppercase tracking-[0.18em] border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        select
                          ? (notified
                              ? "border-[#DDA94E]/40 bg-[#DDA94E]/10 text-[#6E521A] hover:bg-[#DDA94E]/20"
                              : "border-[#DDA94E]/60 text-[#6E521A] hover:bg-[#DDA94E] hover:text-black")
                          : (notified
                              ? "border-charcoal/40 bg-charcoal/5 text-charcoal hover:bg-charcoal/10"
                              : "border-oxblood/50 text-oxblood hover:bg-oxblood hover:text-bone")
                      }`}
                      data-testid={`notify-me-btn-${p.slug}`}
                    >
                      {notified ? <BellOff size={12} strokeWidth={2.5} /> : <BellRing size={12} strokeWidth={2.5} />}
                      {busy ? "…" : notified ? "You'll be notified" : "Notify me when it's back"}
                    </button>
                  );
                })()
              )}

              {/* Category + iter204.5 inventory row */}
              <div className={`border-t ${select ? "mt-3 pt-2.5 border-[#D6C8A3]" : "mt-4 pt-3 border-black/10"}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className={`text-[13px] font-mono uppercase tracking-[0.2em] font-medium ${select ? "text-[#6E521A]" : "text-charcoal/85"}`}>
                    {p.category}
                  </span>
                  {!select && !outOfStock && (() => {
                    const total = totalActualStock(p);
                    if (total === null || total <= 0) return null;
                    return (
                      <span
                        className="inline-flex items-center text-[15px] font-mono uppercase tracking-[0.18em] font-bold text-emerald-700"
                        data-testid={`vials-in-stock-${p.slug}`}
                        title="Actual stock (IMS)"
                      >
                        <span className={`inline-block h-[18px] w-px mr-4 ${select ? "bg-[#DDA94E]/70" : "bg-charcoal/30"}`} aria-hidden="true" />
                        <Package
                          size={17}
                          strokeWidth={2}
                          className="mr-2 text-emerald-700"
                          aria-hidden="true"
                        />
                        {total} vials in stock
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Discreet mailing-list opt-in for logged-in buyers who haven't subscribed yet — never surfaced to temp preview accounts. */}
      {!isTempViewer && <OptInNudge />}
    </div>
  );
}
